<template>
  <div class="container">
    <h2 id="topography">2. Topography</h2>
    <div class="row">
      <div class="col-sm-12">
        <p>
          Topography describes the shape and features of land surfaces. The topography of an area usually also affects other processes such as precipitation and temperature, as higher areas are usually cooler and receive more precipitation than lower areas. Digital models of the surface can also be used to calculate topographic derivates such as slope (steepness) and aspect (direction) of a surface, or to estimate which direction water will flow and how much terrain lies upstream or downstream from a particular point.
        </p>
      </div>
    </div>

    <div class="row justify-content-md-center" v-if="loaded">
      <div class="col-lg-12">
        <h3>2.1 Elevation</h3>
        <p>
          There are several global elevation datasets available at a range of different resolutions. Most commonly used are the SRTM (30m and 90m resolution) produced by NASA on a Space Shuttle mission in 2000 [<a href="#ref-3">3</a>], and the ASTER GDEM dataset (30m resolution) which has been created using photogrammetric techniques. Recently other global elevation models have also been released, most notably ALOS World 3D (30m) from the Japanese Space Agency (JAXA) [<a href="#ref-14">14</a>], and the Copernicus DEM (30m) from the EU Copernicus Programme [<a href="#ref-15">15</a>].
        </p>
        <p v-if="factsheet.data['copernicus_dem']">
          Elevation data for this area of interest is collected from the Copernicus DEM elevation dataset at 30m spatial resolution, which is shown as the default layer in the map below. The elevation in the area ranges from a minimum of <strong>{{ factsheet.data.copernicus_dem.stats.min.toFixed(0)}}m</strong> to a maximum of <strong>{{ factsheet.data.copernicus_dem.stats.max.toFixed(0)}}m</strong>, with an average elevation of <strong>{{ factsheet.data.copernicus_dem.stats.mean.toFixed(0)}}m</strong>. 

          While the range between minimum and maximum elevation is <strong>{{(factsheet.data.copernicus_dem.stats.max-factsheet.data.copernicus_dem.stats.min).toFixed(0)}}m</strong>, the largest part (90%) of the area has an elevation of between <strong>{{ factsheet.data.copernicus_dem.stats.percentiles.q05.toFixed(0)}}m</strong> and <strong>{{ factsheet.data.copernicus_dem.stats.percentiles.q95.toFixed(0)}}m</strong>.
        </p>
        <p v-else-if="factsheet.data['opentopo_srtm_gl1']">
          Elevation data for this area of interest is collected from the SRTM GL1 v3 elevation dataset at 30m spatial resolution. The elevation in the area ranges from a minimum of <strong>{{ factsheet.data.opentopo_srtm_gl1.stats.min.toFixed(0)}}m</strong> to a maximum of <strong>{{ factsheet.data.opentopo_srtm_gl1.stats.max.toFixed(0)}}m</strong>, with an average elevation of <strong>{{ factsheet.data.opentopo_srtm_gl1.stats.mean.toFixed(0)}}m</strong>. 

          While the range between minimum and maximum elevation is <strong>{{(factsheet.data.opentopo_srtm_gl1.stats.max-factsheet.data.opentopo_srtm_gl1.stats.min).toFixed(0)}}m</strong>, the largest part (90%) of the area has an elevation of between <strong>{{ factsheet.data.opentopo_srtm_gl1.stats.percentiles.q05.toFixed(0)}}m</strong> and <strong>{{ factsheet.data.opentopo_srtm_gl1.stats.percentiles.q95.toFixed(0)}}m</strong>.
        </p>
        <p v-else>
          No elevation information could be found for this location. Both the Copernicus DEM as well as the SRTM dataset did not return any results.
        </p>
        <p>
          In addition to the Copernicus DEM dataset, elevation data from the Alos World DEM and the SRTM DEM are also included. Make sure to evaluate the different DEMs critically before using them for further analysis.
        </p>
      </div>
    </div>

    <div class="row justify-content-md-center">
      <div class="col-lg-12">
        <RasterMap 
          backgrounds="streets,satellite,light,osm" 
          title="Digital Elevation Models (DEM)" 
          attribution="Map: Geofolio. Data: SRTM GL1 v3 via OpenTopography [3]. JAXA ALOS World 3d [14]. Copernicus DEM [15]. Backgrounds: Mapbox/Openstreetmap Contributors [13]." 
          v-bind:layout="{legendWidth:5, legendCols:2, descriptionWidth:7, order:['legend','description'] }"
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          v-bind:layers="layers">
          <p>
            Elevation data from various global elevation models are available to view and download in this map. Significant differences may occur between these datasets, therefore you must compare their properties (year, missing data, incorrect values, etc) to determine which is most suitable for your purpose.
          </p>
        </RasterMap>
      </div>
    </div>


  </div>
</template>


<script>
import RasterMap from '@/components/factsheet/RasterMap.vue'

export default {
    name: 'Topography',
    components:{
        RasterMap
    },
    props: ['location', 'factsheet', 'loaded'],
    data() {
        return {
          'layers': [
            {'title': 'Copernicus DEM (2020)', 'source': '/copernicus_dem/copernicus_DEM.tif', 'long_description': "<p><strong>Copernicus DEM</strong> is a recent global elevation dataset at 30m resolution from around 2019/2020. The dataset is a Digital Surface Model (DSM), meaning that elevations of buildings, vegetation, and infrastructure will show up in the dataset. Copernicus DEM was obtained using radar measurements from the TanDEM-X satellite and has a vertical accuracy of &lt; 4 meters [15].</p>"},
            {'title': 'ALOS World (2016)', 'source': '/jaxa_alos_world_30m/jaxa_alos_world_30m.tif', 'long_description': "<p><strong>ALOS World DEM</strong> is produced by the Japanese Space Agency based on sterographic images obtained around 2016. It is a Digital Surface Model (DSM) with a spatial resolution of approx. 30m.</p>"},
            {'title': 'SRTM (2000)', 'source': '/opentopo_srtm_gl1/opentopo_srtm_gl1.tif', 'long_description': "<p><strong>SRTM DEM</strong> is from the GL version 3 dataset and was collected during a Space Shuttle mission in February 2000. Data is available in latitudes between 60 degrees North and 56 degrees South. GL1 Version 3 is the latest version at 30m resolution, in which voids in the dataset have been filled with ASTER GDEM2, the USGS GMTED2010, and USGS NED datasets.</p>"},
          ]
        }
    },
    created() {
    },
    computed: {
    },
    mounted() {
    }
}
</script>

<style>

</style>

          
          